import React, { FunctionComponent, ReactNode } from 'react';
import { Box } from '@mui/material';
import { Typography } from './Typography';

export type PieLegendItem = {
  /** Swatch colour for this entry. */
  color: string;
  /** Main label rendered on the first line. */
  label: ReactNode;
  /** Optional secondary line rendered smaller below the label. */
  sub?: ReactNode;
};

export type PieLegendProps = {
  /** Entries to render, top-to-bottom (or left-to-right when horizontal). */
  items: PieLegendItem[];
  /**
   * Layout direction. `vertical` renders entries stacked (one per row);
   * `horizontal` renders them in a wrapping row — useful under a bar chart.
   * @defaultValue vertical
   */
  direction?: 'vertical' | 'horizontal';
  /** Extra MUI sx for the outer container. */
  sx?: object;
};

/**
 * Self-contained pie/bar legend.
 *
 * recharts v3 removed the ability to pass a custom `payload` to `<Legend>` (the
 * prop is now ignored at runtime — the payload is pulled from internal context,
 * flattened and re-sorted, which also breaks index→colour mapping). Rather than
 * fight the v3 `content` render-prop with an auto-generated, re-sorted payload,
 * we render the legend ourselves from the chart's source data arrays. This keeps
 * the original entry order and the colour-per-index mapping intact.
 */
export const PieLegend: FunctionComponent<PieLegendProps> = ({items, direction = 'vertical', sx}) => {
  if (!items || items.length === 0) {
    return null;
  }

  const isHorizontal = direction === 'horizontal';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isHorizontal ? 'row' : 'column',
        flexWrap: 'wrap',
        gap: 1.5,
        ...(sx || {})
      }}
    >
      {items.map((item, index) => (
        <Box
          key={`legend-${index}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 0
          }}
        >
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              width: 14,
              height: 14,
              marginRight: 1,
              backgroundColor: item.color,
              border: '1px solid transparent',
              borderColor: item.color,
              flexShrink: 0
            }}
          />
          <Box sx={{display: 'flex', flexDirection: 'column'}}>
            <Typography sx={{fontSize: '0.9rem', lineHeight: 1.2}}>{item.label}</Typography>
            {!!item.sub && (
              <Typography sx={{fontSize: '0.8rem', lineHeight: 1.2, opacity: 0.75}}>{item.sub}</Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
